"use client"

import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { Image } from "@/app/components/image"
import type { BlogPost } from "@anaconda/core/blog/types"
import { Trans } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useBlogSpotlight } from "./use-blog-spotlight"

export const BlogSpotlightView = reatomComponent(
  ({ className }: { className?: string }) => {
    const { selectedBlockchain } = useMolecule(BlockchainFiltersMoleculeReatom)
    const blockchain = selectedBlockchain()

    // Only show on "ALL" blockchain view
    if (blockchain !== "ALL") return null

    return (
      <div className={cn("mb-8 flex flex-col", className)}>
        <div className="flex items-center justify-between">
          <h4 className="mb-8 font-headline text-xl">
            <Trans>News</Trans>
          </h4>
          <a
            href="https://rarible.com/blog/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Trans>View all posts →</Trans>
          </a>
        </div>
        <Posts />
      </div>
    )
  },
  "BlogSpotlightView",
)

const POSTS_COUNT = 6

function Posts() {
  const { posts } = useBlogSpotlight()
  const featuredPost = posts[0]
  const restPosts = posts.slice(1, POSTS_COUNT)

  return (
    <PostsLayout
      featuredPost={<FeaturedPost post={featuredPost} />}
      restPosts={restPosts.map((post) => <Post key={post.url} post={post} />)}
    />
  )
}

export function PostsSkeleton() {
  return (
    <PostsLayout
      featuredPost={<FeaturedPostSkeleton />}
      restPosts={
        <>
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
        </>
      }
    />
  )
}

function PostsLayout({
  featuredPost,
  restPosts,
}: { featuredPost: React.ReactNode; restPosts: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-x-14 gap-y-12 md:flex-row">
      <div className="flex-1">{featuredPost}</div>
      <div className="flex flex-1 flex-col gap-y-6">{restPosts}</div>
    </div>
  )
}

function Post({ post }: { post: BlogPost }) {
  return (
    <a href={post.url} target="_blank" rel="noopener noreferrer">
      <PostLayout
        image={
          <Image
            containerClassName="h-full w-full transition-transform duration-300 ease-out group-hover:scale-105"
            className="object-cover"
            imageSrc={post.image}
            alt={post.title}
          />
        }
        info={
          <>
            <span className="text-3xs text-brand uppercase">
              {post.category}
            </span>
            <h6 className="font-headline text-lg">{post.title}</h6>
          </>
        }
      />
    </a>
  )
}

function PostSkeleton() {
  return (
    <PostLayout
      image={<Skeleton className="h-full w-full" />}
      info={
        <>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-6 w-40" />
        </>
      }
    />
  )
}

function PostLayout({
  image,
  info,
}: { image: React.ReactNode; info: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-x-6 ">
      <div className="flex max-w-[420px] flex-grow flex-col gap-y-2">
        {info}
      </div>
      <div className="group aspect-video w-[120px] flex-shrink-0 overflow-hidden rounded-sm border border-white/10 md:w-[160px]">
        {image}
      </div>
    </div>
  )
}

function FeaturedPostSkeleton() {
  return (
    <FeaturedPostLayout
      image={<Skeleton className="h-full w-full" />}
      info={
        <>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-6 w-40" />
        </>
      }
    />
  )
}

function FeaturedPost({ post }: { post: BlogPost }) {
  return (
    <a href={post.url} target="_blank" rel="noopener noreferrer">
      <FeaturedPostLayout
        image={
          <Image
            containerClassName="h-full w-full transition-transform duration-300 ease-out group-hover:scale-105"
            className="object-cover"
            imageSrc={post.image}
            alt={post.title}
          />
        }
        info={
          <>
            <span className="text-3xs text-brand uppercase">
              {post.category}
            </span>
            <h6 className="font-headline text-xl">{post.title}</h6>
            {post.subtitle && (
              <p className="text-body text-white/60">{post.subtitle}</p>
            )}
          </>
        }
      />
    </a>
  )
}

function FeaturedPostLayout({
  info,
  image,
}: { info: React.ReactNode; image: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-y-6">
      <div className="group aspect-video w-full self-stretch overflow-hidden rounded-lg border border-white/10 md:rounded-xl">
        {image}
      </div>
      <div className="flex flex-col gap-y-3">{info}</div>
    </div>
  )
}
